"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowUpRight, GraduationCap, X } from "lucide-react";
import { cn } from "../lib/utils";
import type { RelatedTheoryLink } from "../lib/related-theory";

interface RelatedTheoryProps {
  /**
   * 풀이 상단에 노출할 관련 이론 링크 목록.
   * @example [{ href: "/theory/db/normalization", title: "정규화와 이상현상" }]
   */
  links: RelatedTheoryLink[];
  /** 삽입 위치별 sticky offset 등 미세조정용(선택). 컨테이너 div 에 머지된다. */
  className?: string;
}

/**
 * 문제 풀이(solution) 상단에 고정되어 "관련 이론" 페이지 링크를 칩으로 모아 보여주는 바 (JCG-2320).
 *
 * - sticky `top-0` + 반투명/블러 배경으로 풀이를 스크롤해도 항상 상단에 떠 아래 콘텐츠를 가린다.
 * - 접으면 작은 재펼침 칩만 남아 복구 가능(상태는 인스턴스별 `useState`, 영속화하지 않음 — 리마운트 시 다시 펼쳐짐).
 * - 액센트는 indigo: 풀이(green)·토론(violet)·오답노트(blue)·프리미엄(amber)·문제저장(red/yellow)과 구별되는 학습 톤.
 * - 색상 페어는 dark-mode-colors.md §2/§3(SectionLevel) 규칙을 따른다: light `100/700/300` ↔ dark `950/50, 400, 800`.
 */
export default function RelatedTheory({ links, className }: RelatedTheoryProps) {
  const [collapsed, setCollapsed] = useState(false);

  if (!links || links.length === 0) {
    return null;
  }

  // 접힌 상태: 작은 재펼침 칩만 노출 (완전 제거가 아니라 복구 가능).
  if (collapsed) {
    return (
      <div
        className={cn(
          // mb-3: 아래 풀이 본문과 시각적으로 분리(배경 박스 바깥 투명 여백이라 sticky 충돌 없음).
          "sticky top-0 z-10 mb-3 flex justify-start py-1.5",
          className,
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          aria-label="관련 이론 펼치기"
          aria-expanded={false}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium shadow-sm transition-colors",
            "border-indigo-300 bg-indigo-50 text-indigo-700",
            "dark:border-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300",
            "hover:bg-indigo-100 dark:hover:bg-indigo-900/60",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900",
          )}
        >
          <GraduationCap className="size-4 shrink-0" aria-hidden="true" />
          관련 이론
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        // sticky 바: 풀이 위에 얹혀 아래 콘텐츠를 가리도록 불투명에 가까운 배경 + 블러.
        // mb-3: 바 ↔ 바로 아래 풀이 본문 사이 여백(배경 박스 바깥 투명 여백이라 sticky 충돌 없음, JCG-2320).
        "sticky top-0 z-10 mb-3 rounded-lg border px-3 py-2 shadow-sm backdrop-blur-sm",
        "border-indigo-200 bg-indigo-50/95 text-indigo-700",
        "dark:border-indigo-900 dark:bg-indigo-950/80 dark:text-indigo-300",
        className,
      )}
      data-llm-copy-skip="true"
    >
      <div className="flex items-start gap-2">
        {/* 선행 라벨 + 아이콘 */}
        <span className="mt-1 inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-indigo-700 dark:text-indigo-300">
          <GraduationCap className="size-4 shrink-0" aria-hidden="true" />
          관련 이론
        </span>

        {/* 칩 목록: 여러 개면 줄바꿈 (min-w-0 으로 긴 제목이 컨테이너를 넘치지 않게) */}
        <ul className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {links.map((link) => (
            <li key={link.href}>
              <Link
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-sm font-medium transition-colors",
                  "border-indigo-300 bg-white text-indigo-700",
                  "dark:border-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200",
                  "hover:bg-indigo-100 dark:hover:bg-indigo-900/70",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900",
                )}
              >
                <span className="break-words">{link.title}</span>
                <ArrowUpRight
                  className="size-3.5 shrink-0 text-indigo-500 dark:text-indigo-400"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}
        </ul>

        {/* 접기 버튼 */}
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          aria-label="관련 이론 접기"
          aria-expanded={true}
          className={cn(
            "mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-md transition-colors",
            "text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700",
            "dark:text-indigo-400 dark:hover:bg-indigo-900/70 dark:hover:text-indigo-200",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900",
          )}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
